import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BoldHeader from './BoldHeader';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        submit: error.message || 'Registration failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-jet-black">
      <BoldHeader />
      
      <div className="flex items-center justify-center min-h-screen pt-20">
        <div className="max-w-md w-full mx-6">
          <div className="text-center mb-12">
            <h1 className="display-hero mb-6">
              JOIN THE <span className="text-neon-green">ELITE</span>
            </h1>
            <p className="text-energy text-xl mb-8">
              Create your account and unlock your potential with AI coaching.
              Your transformation starts NOW.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 rounded-3xl p-8 neon-border backdrop-blur-sm">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-power">
                <span className="text-white font-black text-2xl">AI</span>
              </div>
              <h2 className="heading-bold text-white mb-4">
                UNLEASH YOUR FULL POTENTIAL
              </h2>
            </div>

            {errors.submit && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-2xl">
                <p className="text-red-200 text-sm text-center">{errors.submit}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 transition-all ${
                    errors.name 
                      ? 'border-red-500/50 focus:border-red-400' 
                      : 'border-gray-600 focus:border-electric-blue/50'
                  }`}
                />
                {errors.name && <p className="text-red-400 text-sm mt-2 ml-2">{errors.name}</p>}
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 transition-all ${
                    errors.email 
                      ? 'border-red-500/50 focus:border-red-400' 
                      : 'border-gray-600 focus:border-electric-blue/50'
                  }`}
                />
                {errors.email && <p className="text-red-400 text-sm mt-2 ml-2">{errors.email}</p>}
              </div>

              <div>
                <input
                  type="password"
                  name="password"
                  placeholder="Password (min 6 characters)"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 transition-all ${
                    errors.password 
                      ? 'border-red-500/50 focus:border-red-400' 
                      : 'border-gray-600 focus:border-electric-blue/50'
                  }`}
                />
                {errors.password && <p className="text-red-400 text-sm mt-2 ml-2">{errors.password}</p>}
              </div>

              <div>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-6 py-4 rounded-2xl text-lg outline-none border-2 transition-all ${
                    errors.confirmPassword 
                      ? 'border-red-500/50 focus:border-red-400' 
                      : 'border-gray-600 focus:border-electric-blue/50'
                  }`}
                />
                {errors.confirmPassword && <p className="text-red-400 text-sm mt-2 ml-2">{errors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-power py-4 text-lg mb-6 hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
              >
                <span className="relative z-10">
                  {loading ? '🚀 CREATING YOUR ACCOUNT...' : '🚀 JOIN THE ELITE'}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
            </form>

            <div className="text-center mt-8 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm mb-4">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-neon-green font-bold hover:text-electric-blue transition-colors"
                >
                  Sign In
                </button>
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                <span>🔒 Bank-Level Security</span>
                <span>•</span>
                <span>⚡ Instant Access</span>
                <span>•</span>
                <span>✨ Free to Start</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;